import { useState } from 'react'

import { beakerLevel, ru } from '../../lib/physics'
import { Choice, Predict, Slider } from './common'

/**
 * Объём погружением (урок 6): тело на нити опускают в мензурку, уровень поднимается.
 * Отсчёт — по нижнему краю мениска; объём тела V = V₂ − V₁. Сколько воды налито вначале,
 * для ответа не важно.
 */

const ТЕЛА = [
  { имя: 'камень', объём: 75 },
  { имя: 'флакон', объём: 40 },
  { имя: 'гайка', объём: 15 },
] as const
const ВМЕСТИМОСТЬ = 500
const ДНО = 260
const ВЕРХ = 30
const pxНаМл = (ДНО - ВЕРХ) / ВМЕСТИМОСТЬ

export default function BeakerLab() {
  const [тело, setТело] = useState(0)
  const [v1, setV1] = useState(335)
  const [глубина, setГлубина] = useState(0) // доля тела под водой, %

  const т = ТЕЛА[тело]
  const v2 = beakerLevel(v1, т.объём, глубина / 100, ВМЕСТИМОСТЬ)
  const yУр = (v: number) => ДНО - v * pxНаМл
  const размер = Math.cbrt(т.объём) * 7 // сторона тела на рисунке
  const низТела = yУр(v1) - 20 + (глубина / 100) * (размер + 30)
  const погружено = глубина >= 100

  return (
    <div className="card">
      <div className="lab-two">
        <svg className="lab-svg" style={{ maxWidth: 240, margin: '0 auto' }} viewBox="0 0 200 280" role="img" aria-label="Мензурка с телом на нити">
          <rect x={50} y={yУр(v2)} width={90} height={ДНО - yУр(v2)} className="fill-earth" />
          {/* мениск: вода у стенок чуть выше */}
          <path d={`M50 ${yУр(v2) - 4} Q95 ${yУр(v2) + 6} 140 ${yУр(v2) - 4}`} className="ink-el" fill="none" strokeWidth={1.5} />
          <rect x={50} y={ВЕРХ - 10} width={90} height={ДНО - ВЕРХ + 10} className="ink" fill="none" strokeWidth={2} />
          {Array.from({ length: ВМЕСТИМОСТЬ / 10 + 1 }, (_, i) => (
            <g key={i}>
              <line x1={140} y1={yУр(i * 10)} x2={i % 5 === 0 ? 126 : 132} y2={yУр(i * 10)} className="ink" strokeWidth={0.7} />
              {i % 10 === 0 && <text x={146} y={yУр(i * 10) + 3} className="lab-tick">{i * 10}</text>}
            </g>
          ))}
          <text x={146} y={ВЕРХ - 14} className="lab-tick">мл</text>
          <line x1={95} y1={0} x2={95} y2={низТела - размер} className="ink" strokeWidth={1} />
          <rect x={95 - размер / 2} y={низТела - размер} width={размер} height={размер} rx={размер / 4} className="fill-muted ink" strokeWidth={1} />
        </svg>

        <div>
          <div className="lab-readout lab-readout-col">
            <span>до погружения V₁ = {ru(v1, 0)} мл</span>
            <span className="el">сейчас V₂ = {ru(v2, 0)} мл</span>
            <span>
              V = V₂ − V₁ = {погружено ? <b>{ru(v2 - v1, 0)} см³</b> : 'тело ещё не под водой целиком'}
            </span>
          </div>
          <Slider label="Опустить тело" value={глубина} min={0} max={100} step={1} unit="%" onChange={setГлубина} />
          <Slider label="Воды в мензурке вначале" value={v1} min={150} max={400} step={5} unit="мл" onChange={(v) => { setV1(v); setГлубина(0) }} />
        </div>
      </div>

      <Predict
        question="Было 335 мл, после погружения камня стало 410 мл. Каков объём камня?"
        options={['410 см³', '335 см³', '75 см³']}
        right={2}
        revealed={погружено && т.имя === 'камень' && v1 === 335}
        explain="Камень вытеснил воду, равную своему объёму: 410 − 335 = 75 мл. 1 мл = 1 см³, объём твёрдого тела пишут в кубических сантиметрах."
      />

      <div className="lab-sub">Тело</div>
      <Choice options={ТЕЛА.map((t, i) => ({ value: i, label: t.имя }))} value={тело} onChange={(v) => { setТело(v); setГлубина(0) }} />
      <p className="muted small">
        Отсчёт — по нижнему краю мениска: вода у стенок приподнята. Налей вначале больше или меньше —
        разность V₂ − V₁ всё равно та же. Пористый мел так не измерить: он впитает воду.
      </p>
    </div>
  )
}
